import React, { useEffect, useRef } from 'react';

import MessageBubble from './MessageBubble';
import ToolActivity from './ToolActivity';
import type { ChatMessage } from './types';

export interface MessageListProps {
  messages: ChatMessage[];
  /** Whether the chat has been persisted (drives per-message timestamps). */
  persisted: boolean;
  /** Timestamp used for every message of a not-yet-persisted chat. */
  sendMessageTimestamp?: Date;
  running: boolean;
  stage: string;
  /** Streaming narration is the trailing ASSISTANT message while running. */
  streaming: boolean;
  /** Scroll to bottom when this flips to true (run finished). */
  scrollToEndSignal: boolean;
  onRedo: (index: number) => void;
  onCopy: (message: ChatMessage) => void;
  onDownload: (message: ChatMessage) => void;
  onCancel?: () => void;
  onFetchFileUrl: (
    fileId: string
  ) => Promise<{ url: string; fileName: string; mimetype: string }>;
  /** Rendered when there are no messages. */
  emptyState: React.ReactNode;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  persisted,
  sendMessageTimestamp,
  running,
  stage,
  streaming,
  scrollToEndSignal,
  onRedo,
  onCopy,
  onDownload,
  onCancel,
  onFetchFileUrl,
  emptyState,
}) => {
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollToEndSignal) {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [scrollToEndSignal, messages.length]);

  const lastIndex = messages.length - 1;

  return (
    <div
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
      aria-label="PRATUS AI conversation"
      data-testid="ai-message-list"
      data-tour="pratus-ai-sources"
      className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto tw:px-3 tw:py-3 tw:[scrollbar-gutter:stable]"
    >
      {messages.length === 0
        ? emptyState
        : messages.map((message, index) => (
            <MessageBubble
              key={`dpgt-message-${index}`}
              message={message}
              index={index}
              timestamp={persisted ? message.timestamp : sendMessageTimestamp}
              streaming={
                streaming &&
                index === lastIndex &&
                message.role.toUpperCase() === 'ASSISTANT'
              }
              onRedo={onRedo}
              onCopy={onCopy}
              onDownload={onDownload}
              onFetchFileUrl={onFetchFileUrl}
            />
          ))}
      <ToolActivity running={running} stage={stage} onCancel={onCancel} />
      <div ref={endRef} />
    </div>
  );
};

export default MessageList;
